/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { TourService } from '../tour.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { TourDaoService } from '../../dao/tour-dao.service';
import { IMSId } from 'src/app/_model/ims-object';
import { AppService } from 'src/app/_services/app-service/logic/app.service';
import { CredentialsService } from 'src/app/_services/credentials/logic/credentials.service';
import { LocationService } from 'src/app/_services/location/location.service';
import { Tour, TourDTO } from 'src/app/_model/tour';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';

/**
 * Loads Tour
 */
@Injectable()
export class TourLogicService implements TourService {
  public tours$ = new BehaviorSubject<Tour[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private tourDaoService: TourDaoService,
    private appService: AppService,
    private credentialService: CredentialsService,
    private locationService: LocationService,
    private localStatusService: LocalStatusService,
    private appParameterService: AppParameterService,
  ) {
    this.initialize();
  }

  // Public Function

  public async updateTours(toursDto: TourDTO[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new tours from other IMS component',
      toursDto?.length,
    );

    try {
      let tours: Tour[] = [];

      for (const tourDto of toursDto) {
        let localStatus = await this.localStatusService.loadLocalTourStatus({
          imsId: tourDto.id,
          secondaryId: undefined,
          objRef: 'ims/Tour',
        });
        console.log('JBupdateTours localStatus', localStatus);
        tours.push(
          Tour.fromDto(
            tourDto,
            localStatus,
            undefined,
            this.appParameterService.getFormatLabelForObject('ims/Tour'),
          ),
        );
      }

      await this.tourDaoService.saveTours(tours);
      await this.tours$.next(tours);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing Tours: ',
        error.message,
      );
      throw error;
    }
  }

  public getTourById(id: IMSId): Tour {
    if (id === undefined) {
      return undefined;
    }

    return this.tours$.getValue().find((tour) => {
      if (
        tour.id.imsId === id.imsId &&
        tour.id.secondaryId === id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public async updateTour(tour: Tour) {
    let tours = this.tours$.getValue();
    for (let t of tours) {
      if (
        t.id.imsId === tour.id.imsId ||
        t.id.secondaryId === tour.id.secondaryId
      ) {
        t = tour;
      }
    }
    this.tours$.next(tours);
    await this.tourDaoService.saveTours(tours);
  }

  public async addTour(tour: Tour) {
    let tours = this.tours$.getValue();
    tours.push(tour);
    this.tours$.next(tours);
    await this.tourDaoService.saveTours(tours);
  }

  // Resetable
  public async reset() {
    await this.tourDaoService.reset();
    await this.loadLocalTours();
  }

  public async clear() {
    await this.tourDaoService.clear();
    this.tours$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel): Promise<void> {}

  public async presync(): Promise<void> {}

  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalTours();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalTours() {
    try {
      this.logger.log(this.constructor.name, '- Load local Tours');
      let tours: Tour[] = await this.tourDaoService.getTours();
      this.logger.log(
        this.constructor.name,
        '- Loaded local Tours',
        tours.length,
      );
      this.tours$.next(tours);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local tours: ',
        error.message,
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {}
}
